import { CHAT_LIMITS, countCodePoints } from '@screenshare/protocol';
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { linkifyChatText, type DataChannelManager, type ChatEntry } from '../chat';

export interface ChatPanelProps {
  readonly chat: DataChannelManager;
  readonly initiallyCollapsed?: boolean;
  readonly title?: string;
}

export function ChatPanel({
  chat,
  initiallyCollapsed = false,
  title = 'Session chat',
}: ChatPanelProps) {
  const [snapshot, setSnapshot] = useState(chat.snapshot);
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);
  const [draft, setDraft] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const messageList = useRef<HTMLDivElement>(null);
  const characterCount = countCodePoints(draft);
  const available = snapshot.state === 'open';

  useEffect(() => chat.subscribe(setSnapshot), [chat]);

  useEffect(() => {
    chat.setPanelVisible(!collapsed);
    return () => chat.setPanelVisible(false);
  }, [chat, collapsed]);

  useEffect(() => {
    if (!collapsed) {
      messageList.current?.scrollTo({ top: messageList.current.scrollHeight });
    }
  }, [collapsed, snapshot.messages.length]);

  const handleInput: JSX.GenericEventHandler<HTMLTextAreaElement> = (event) => {
    const value = event.currentTarget.value;
    setDraft(value);
    setFeedback(null);
    chat.setTyping(value.trim().length > 0);
  };

  const submit = () => {
    const result = chat.sendText(draft);
    if (result.ok) {
      setDraft('');
      setFeedback(null);
      return;
    }
    setFeedback(sendFailureMessage(result.reason));
  };

  const handleSubmit: JSX.SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown: JSX.KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  if (collapsed) {
    return (
      <aside class="chat-panel chat-panel--collapsed" aria-label={title}>
        <button
          class="chat-panel__reopen"
          type="button"
          aria-expanded="false"
          onClick={() => setCollapsed(false)}
        >
          <ChatIcon />
          <span>Chat</span>
          {snapshot.unreadCount > 0 && (
            <span class="chat-panel__badge" aria-label={`${snapshot.unreadCount} unread messages`}>
              {formatUnreadCount(snapshot.unreadCount)}
            </span>
          )}
        </button>
      </aside>
    );
  }

  return (
    <aside class="chat-panel" aria-label={title}>
      <header class="chat-panel__header">
        <div>
          <h2>{title}</h2>
          <p class={`chat-panel__status chat-panel__status--${snapshot.state}`}>
            {statusLabel(snapshot.state)}
          </p>
        </div>
        <button
          class="chat-panel__collapse"
          type="button"
          aria-label="Collapse chat"
          aria-expanded="true"
          onClick={() => setCollapsed(true)}
        >
          <CollapseIcon />
        </button>
      </header>

      <div
        class="chat-panel__messages"
        ref={messageList}
        aria-live="polite"
        aria-relevant="additions"
      >
        {snapshot.messages.length === 0 ? (
          <div class="chat-panel__empty">
            <ChatIcon />
            <strong>No messages yet</strong>
            <span>Messages stay between the two browsers and disappear with this session.</span>
          </div>
        ) : (
          snapshot.messages.map((message) => <ChatBubble key={message.id} message={message} />)
        )}
      </div>

      <div class="chat-panel__typing" role="status" aria-live="polite">
        {snapshot.peerTyping ? 'The other person is typing…' : '\u00a0'}
      </div>

      <form class="chat-panel__composer" onSubmit={handleSubmit}>
        <label class="sr-only" for="chat-message">
          Message
        </label>
        <textarea
          id="chat-message"
          value={draft}
          rows={2}
          placeholder={available ? 'Write a message…' : 'Chat unavailable'}
          disabled={!available}
          aria-describedby="chat-composer-meta"
          onInput={handleInput}
          onKeyDown={handleKeyDown}
        />
        <div class="chat-panel__composer-footer" id="chat-composer-meta">
          <span class={characterCount > CHAT_LIMITS.messageCodePoints ? 'is-over-limit' : ''}>
            {characterCount.toLocaleString()} / {CHAT_LIMITS.messageCodePoints.toLocaleString()}
          </span>
          <button
            type="submit"
            disabled={
              !available || draft.trim() === '' || characterCount > CHAT_LIMITS.messageCodePoints
            }
          >
            Send
          </button>
        </div>
        {feedback !== null && (
          <p class="chat-panel__feedback" role="alert">
            {feedback}
          </p>
        )}
      </form>
    </aside>
  );
}

function ChatBubble({ message }: { readonly message: ChatEntry }) {
  return (
    <article class={`chat-message chat-message--${message.sender}`}>
      <div class="chat-message__bubble">
        {linkifyChatText(message.text).map((segment, index) =>
          segment.type === 'link' ? (
            <a
              key={`${segment.href}-${index}`}
              href={segment.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {segment.value}
            </a>
          ) : (
            segment.value
          ),
        )}
      </div>
      <time dateTime={new Date(message.sentAt).toISOString()}>{formatTime(message.sentAt)}</time>
    </article>
  );
}

function statusLabel(state: DataChannelManager['state']): string {
  switch (state) {
    case 'open':
      return 'Private · Connected';
    case 'connecting':
      return 'Connecting…';
    case 'reconnecting':
      return 'Reconnecting…';
    case 'failed':
      return 'Temporarily unavailable';
    case 'ended':
      return 'Session ended';
    case 'idle':
      return 'Waiting for session';
  }
}

function sendFailureMessage(reason: 'empty' | 'too-long' | 'unavailable' | 'send-failed'): string {
  switch (reason) {
    case 'empty':
      return 'Enter a message first.';
    case 'too-long':
      return `Messages are limited to ${CHAT_LIMITS.messageCodePoints.toLocaleString()} characters.`;
    case 'unavailable':
      return 'Chat is reconnecting. Try again in a moment.';
    case 'send-failed':
      return 'The message could not be sent. Try again.';
  }
}

function formatTime(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(
    timestamp,
  );
}

function formatUnreadCount(count: number): string {
  return count > 99 ? '99+' : String(count);
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5.5 4.75h13a2.75 2.75 0 0 1 2.75 2.75v7a2.75 2.75 0 0 1-2.75 2.75h-7.1l-4.9 3v-3h-1A2.75 2.75 0 0 1 2.75 14.5v-7A2.75 2.75 0 0 1 5.5 4.75Z" />
    </svg>
  );
}

function CollapseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m7 10 5 5 5-5" />
    </svg>
  );
}
